import { tallyTokens } from "../../theme.tsx";
import "../../styles/animation-conversation.css";
import React, { useRef } from "react";
import { Trans } from "@lingui/react/macro";
import mainTheme from "../../theme.tsx";
import { Box, ThemeProvider, useMediaQuery, useTheme } from "@mui/material";
import SearchField from "../search/SearchField.tsx";
import Conversation from "./Conversation.tsx";
import ExampleItems from "../example/ExampleItems.tsx";
import { useTypedSelector } from "../../hooks.tsx";
import ExchangePanel from "./Exchange.tsx";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";
import { CONVERSATION_LAYOUT } from "./constants.ts";
import {
  getSelectedConversationExchanges,
  getSelectedDashboard,
} from "../../store/conversation.tsx";
import DashboardView from "./DashboardView.tsx";
import TopBar from "./TopBar.tsx";
import Notification from "../notifications/Notification.tsx";
import { useFeatureFlag } from "../../hooks/useFeatureFlag.tsx";
import { useLocation } from "react-router-dom";

export interface ConversationPanelProps {
  children?: React.ReactNode;
  openDrawer: boolean;
  setOpenDrawer?: React.Dispatch<React.SetStateAction<boolean>>;
}

const EMPTY_EXCHANGE_IDS: string[] = [];

const ConversationPanel: React.FC<ConversationPanelProps> = (props) => {
  const theme = useTheme();
  const location = useLocation();

  // Create ref for the scrollable conversation container
  const conversationContainerRef = useRef<HTMLDivElement>(null);

  const handleDrawerOpen = () => {
    if (props.setOpenDrawer) {
      props.setOpenDrawer(true);
    }
  };

  const { byId: notifications } = useTypedSelector(
    (state) => state.notifications,
  );
  const hasDashboardsFeature = useFeatureFlag("dashboards");

  const selectedConversationExchanges = useTypedSelector(
    getSelectedConversationExchanges,
  );
  const exchangeIds =
    selectedConversationExchanges?.allIds || EMPTY_EXCHANGE_IDS;
  const selectedView = useTypedSelector(
    (state) => state.conversationState.selectedView,
  );

  const conversations = useTypedSelector(
    (state) => state.conversationState.conversations,
  );
  const selectedConversationId = useTypedSelector(
    (state) => state.conversationState.selectedConversation,
  );
  const selectedDashboard = useTypedSelector(getSelectedDashboard);

  const { anonymousSession } = useTypedSelector((state) => state.authState);

  // During the sign up flow, we don't show example items below the search bar
  const showExamples = !(
    anonymousSession &&
    anonymousSession.signUpState &&
    !anonymousSession.signUpState.isComplete
  );

  const isScreenBiggerThanOrEqualToMd: boolean = useMediaQuery(
    theme.breakpoints.up("md"),
  );

  const showNotifications = selectedView === "notifications";
  const showDashboard = hasDashboardsFeature && selectedView === "dashboard";
  const showConversationComposer = selectedView === "conversation";

  const conversationTitle = [
    ...conversations.pinned,
    ...conversations.last_7_days,
    ...conversations.last_30_days,
    ...conversations.other,
  ].find((conversation) => conversation.id === selectedConversationId)?.title;

  const topBarTitle = showNotifications ? (
    <Trans context="Top bar title while the notifications list is shown">
      Notifications
    </Trans>
  ) : showDashboard ? (
    selectedDashboard?.title
  ) : (
    conversationTitle || (
      <Trans context="Top bar title for a conversation that has no title yet">
        New conversation
      </Trans>
    )
  );

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetExchangeId = params.get("exchange");
    if (!targetExchangeId || selectedView !== "conversation") {
      return;
    }

    const timeout = window.setTimeout(() => {
      const container = conversationContainerRef.current;
      if (!container) {
        return;
      }

      const target = Array.from(
        container.querySelectorAll<HTMLElement>("[data-exchange-id]"),
      ).find((element) => element.dataset.exchangeId === targetExchangeId);
      if (!target) {
        return;
      }

      container.scrollTo({
        top: Math.max(0, target.offsetTop - SETTINGS.topMargin),
        behavior: "smooth",
      });
      target.classList.add("exchange-search-highlight");
      window.setTimeout(() => {
        target.classList.remove("exchange-search-highlight");
      }, 1800);
    }, 50);

    return () => window.clearTimeout(timeout);
  }, [exchangeIds, location.search, selectedView]);

  return (
    <ThemeProvider theme={mainTheme}>
      <Box>
        <Box
          sx={[
            {
              // The panel fills the viewport rather than floating as a fixed-width
              // slab. Reading width is constrained on the content inside instead,
              // so the surface reaches the edges on every screen size.
              position: "fixed",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              overflow: "hidden",
              backgroundColor: tallyTokens.canvas,
              flexDirection: "column",
              transition: theme.transitions.create(["left"], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
            },
            // css for screens greater than md-size
            isScreenBiggerThanOrEqualToMd &&
              props.openDrawer && {
                left: `${SETTINGS.drawerWidth}px`,
                transition: theme.transitions.create(["left"], {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              },
          ]}
        >
          {anonymousSession ? (
            <TopBar showBrand />
          ) : (
            <TopBar
              title={topBarTitle}
              onOpenDrawer={props.openDrawer ? undefined : handleDrawerOpen}
            />
          )}
          {/* Scrollable conversation area */}
          <Box
            ref={conversationContainerRef}
            id="conversation-panel-container"
            sx={[
              {
                display: "flex",
                flexDirection: "column",
                flex: 1, // Allow this box to grow and shrink
                width: "100%",
                overflowY: "scroll", // MAIN SCROLLPORT - like ChatGPT
                position: "relative",
                zIndex: 1,
                scrollbarGutter: "stable",
                scrollbarColor:
                  "var(--tally-scroll-thumb) var(--tally-scroll-track)",
                paddingX: {
                  xs: "1rem",
                  sm: "1.5rem",
                  md: "24px",
                }, // Gutter; reading width is capped on the inner wrapper below
                boxSizing: "border-box", // Include padding in width calculation
                // Cap the measure so prose stays readable now the panel is
                // full-bleed. Dashboards are not prose: they fill large
                // screens, up to a cap that keeps ultra-wide monitors sane.
                "& > *": {
                  width: "100%",
                  maxWidth: showDashboard ? "2200px" : "820px",
                  marginInline: "auto",
                },
                // The top bar is in the flow above, so the content only needs
                // the design's own inset below it.
                paddingTop: showDashboard ? "26px" : "30px",
                marginBottom: isScreenBiggerThanOrEqualToMd
                  ? showConversationComposer
                    ? `${SETTINGS.bottomMargin}px`
                    : "40px"
                  : showConversationComposer
                    ? `${SETTINGS.bottomMarginMobile}px`
                    : "32px",
              },
            ]}
          >
            {showNotifications ? (
              <Conversation
                scrollContainerRef={
                  conversationContainerRef as React.RefObject<HTMLDivElement>
                }
              >
                {Object.entries(notifications).map(([id, notification]) => (
                  <Notification
                    key={id}
                    id={id}
                    content={notification.message.text}
                    read={notification.read}
                  />
                ))}
              </Conversation>
            ) : showDashboard ? (
              <DashboardView />
            ) : (
              <Conversation
                scrollContainerRef={
                  conversationContainerRef as React.RefObject<HTMLDivElement>
                }
              >
                {exchangeIds.map((exchangeId) => (
                  <ExchangePanel key={exchangeId} exchangeId={exchangeId} />
                ))}
              </Conversation>
            )}
          </Box>

          {/* Fixed SearchField at bottom - outside scrollable area */}
          {showConversationComposer && (
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                // Fade the canvas in behind the composer so conversation text
                // dissolves under it instead of being cut by a hard edge.
                background: `linear-gradient(to bottom, rgba(247,244,239,0) 0%, ${tallyTokens.canvas} 22%)`,
                paddingX: {
                  xs: "1rem",
                  sm: "1.5rem",
                  md: "2rem",
                  lg: "2.5rem",
                  xl: "3rem",
                }, // Gutter; reading width is capped on the inner wrapper below
                paddingTop: { xs: "28px" },
                paddingBottom: {
                  xs: showExamples
                    ? `${CONVERSATION_LAYOUT.bottomPadding.mobile}`
                    : "56px",
                  sm: showExamples
                    ? `${CONVERSATION_LAYOUT.bottomPadding.tablet}`
                    : "64px",
                  md: showExamples
                    ? `${CONVERSATION_LAYOUT.bottomPadding.desktop}`
                    : "44px",
                },
                zIndex: 1000,
                display: "flex",
                flexDirection: "column",
                gap: { xs: 1.5, sm: 2, md: 2.5 }, // ✅ Responsive spacing: 12px/16px/20px
                alignItems: "center",
                // Match the reading measure used by the conversation above.
                "& > *": { width: "100%", maxWidth: "820px" },
              }}
            >
              <SearchField />
              {showExamples && (
                <Box>
                  <ExampleItems />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default ConversationPanel;
